import { z } from 'zod';

/**
 * Base profile validation schema for all users
 */
const baseProfileSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  
  whatsapp_number: z.string()
    .optional()
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      // Israeli format: +972-XX-XXX-XXXX or international format
      // Allow formats: +972501234567, +972-50-123-4567, 0501234567, etc.
      const phoneRegex = /^(\+?\d{1,3}[-.\s]?)?(\(?\d{2,3}\)?[-.\s]?)?\d{3,4}[-.\s]?\d{4}$/;
      return phoneRegex.test(val.replace(/\s/g, ''));
    }, 'Invalid phone number format'),
  
  bio: z.string()
    .max(160, 'Bio must be 160 characters or less')
    .optional(),
  
  website_url: z.string()
    .optional()
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      try {
        new URL(val);
        return true;
      } catch {
        return false;
      }
    }, 'Invalid URL format'),
});

/**
 * Broker-specific profile schema
 * Extends base schema with broker fields
 */
export const brokerProfileSchema = baseProfileSchema.extend({
  agency_name: z.string().max(100, 'Agency name is too long').optional(),
  license_number: z.string().max(50, 'License number is too long').optional(),
});

/**
 * Renter-specific profile schema
 * Same as base schema (brokers fields hidden in UI)
 */
export const renterProfileSchema = baseProfileSchema.extend({
  agency_name: z.string().optional().default(''),
  license_number: z.string().optional().default(''),
});

/**
 * Type inference for the profile form data
 */
export type ProfileFormData = z.infer<typeof brokerProfileSchema>;

/**
 * Get the appropriate schema based on user role
 */
export function getProfileSchema(role: 'user' | 'admin' | 'broker') {
  // Brokers and admins can edit broker fields
  if (role === 'broker' || role === 'admin') {
    return brokerProfileSchema;
  }
  // Regular users (renters) use base schema
  return renterProfileSchema;
}

/**
 * Validate profile data based on role
 */
export function validateProfileData(data: Partial<ProfileFormData>, role: 'user' | 'admin' | 'broker') {
  const schema = getProfileSchema(role);
  return schema.safeParse(data);
}
